// import EventEmitter from 'eventemitter3';
import { createWorkerManager } from './worker-manager.js';
import { createStatusManager } from './status-manager.js';

import EventEmitter from './utils/buffered-eventemitter';

let videoFrameCallback;
let videoEl;
let countdownSetIntervalId;
let currentTimeLeft;
let vitalsHaveBeenAuthorized;
let authorizedVitals;
let inPreviewMode = false;
let guideCoordinatesSet = false;
let currentSessionSignsMsgs = {}; // Collect signsMessage events during session
let coreVersion = null; // Vital Core (WASM) version string, cached once the worker is ready
let captureTimeUsed = false; // True once a frame used metadata.captureTime (vs Date.now() fallback)

const statusWarningMessageMap = {
  'E-DQM-021': { message: 'Please stay still', code: 'E-DQM-021', severity: 'warning' },
  'E-DQM-022': { message: 'Improve your lighting so your face is evenly lit', code: 'E-DQM-022', severity: 'warning' },
  'E-FDM-041': { message: 'Face not detected', code: 'E-FDM-041', severity: 'warning' },
  'E-FDM-042': { message: 'Please keep face centered', code: 'E-FDM-042', severity: 'warning' },
  'W-RES-073': { message: 'Please keep face centered', code: 'W-RES-073', severity: 'warning' },
  'W-RES-076': { message: 'Please keep face centered', code: 'W-RES-076', severity: 'warning' },
  'W-RES-078': { message: 'Please keep face centered', code: 'W-RES-078', severity: 'warning' },
  'W-RES-079': { message: 'Face too far from camera', code: 'W-RES-079', severity: 'warning' },
  'W-RES-080': { message: 'Face too close to camera', code: 'W-RES-080', severity: 'warning' },
};

// Critical status codes that should immediately terminate the session
const criticalStatusCodes = [
  'C-GEN-003',
  'C-GEN-005',
  'C-VPU-051',
];

const processDataStatus = (dataStatusArr) => {
  return dataStatusArr.map((statusCode) => {
    if (statusWarningMessageMap[statusCode]) {
      return statusWarningMessageMap[statusCode];
    }

    return { code: statusCode, message: '', severity: 'info' };
  });
}

/**
 * Creates a Vital Client - Primary SDK interface
 * @param {Object} config - Configuration object
 * @param {string} config.workerDirectory - Path to worker directory
 * @param {Function} config.authenticator - Callback function(runToken) that returns auth response
 */
export function createVitalClient(config = {}) {
  const emitter = new EventEmitter();
  const clientConfig = {
    ...config,
  };

  let workerManager = null;
  const statusManager = createStatusManager();
  let initialized = false;
  let startTimestamp;
  let maxSessionTime = 0;

  // Max consecutive lost-face restarts before giving up and ending the attempt.
  // The core emits E-FDM-041 once the face has been continuously undetected past
  // its ~1s threshold; we restart the attempt each time, up to this many times in
  // a row without the face being re-acquired, then end the attempt so a consumer
  // (e.g. a retry loop) can surface "couldn't detect a face." Configurable via
  // config.maxFaceLostRestarts.
  const maxFaceLostRestarts = clientConfig.maxFaceLostRestarts ?? 5;

  let brEarlyEndEnabled = false;
  let hrEarlyEndEnabled = false;
  let bpEarlyEndEnabled = false;
  let spo2EarlyEndEnabled = false;
  let brCanEndEarly = false;
  let hrCanEndEarly = false;
  let bpCanEndEarly = false;
  let spo2CanEndEarly = false;

  // Lost-face (E-FDM-041) restart state.
  let restarting = false;           // true while a restart is in flight; frames are ignored
  let faceLostRestartCount = 0;     // consecutive restarts since the face was last valid
  let currentSelectedVitals = null; // remembered so a restart can re-tag the new session

  /**
   * Initialize the SDK and worker
   */
  const init = async () => {
    if (initialized) {
      throw new Error('VitalClient already initialized');
    }

    workerManager = createWorkerManager(clientConfig);

    // Forward worker events
    workerManager.on('ready', () => {
      initialized = true;
      statusManager.updateStatus({ initialized: true });
      emitter.emit('ready');
      // Cache the core version once, so it can be stamped into every stop
      // result without an extra worker round-trip. Best-effort: a failure here
      // must not affect readiness, so it is not awaited and errors are ignored.
      workerManager.callMethod('getVersionString')
        .then((version) => { coreVersion = version; })
        .catch(() => {});
    });

    workerManager.on('error', (error) => {
      emitter.emit('error', error);
    });

    workerManager.on('log', (msg) => {
      emitter.emit('log', msg);
    });

    workerManager.on('signsMessage', (msg) => {
      handleSignsMsgs(msg);
    });

    workerManager.on('segmentationDuration', (data) => {
      emitter.emit('segmentationDuration', data);
    });

    workerManager.on('faceDetectionDuration', (data) => {
      emitter.emit('faceDetectionDuration', data);
    });

    await workerManager.init();
  };

  /**
   * Processes message from the vital core algorithm to determine when to end
   * a session attempt
   */
  const handleSignsMsgs = async (msg) => {
    // While a lost-face restart is in flight the old session is being torn down
    // and a fresh one started. Ignore incoming frames until it completes so they
    // can't race the reset or double-trigger another restart.
    if (restarting) {
      return;
    }

    const { dataStatus } = msg;

    // Store message with timestamp (matches vital-trac-app pattern)
    const currTimestamp = Date.now();
    currentSessionSignsMsgs[currTimestamp] = msg;

    // Check for critical status codes that require immediate session termination
    if (dataStatus) {
      const hasCriticalError = dataStatus.some(code => criticalStatusCodes.includes(code));
      if (hasCriticalError) {
        const criticalCode = dataStatus.find(code => criticalStatusCodes.includes(code));
        emitter.emit('criticalError', { code: criticalCode, message: 'Critical error detected, ending session' });
        await stop();
        return;
      }
    }

    // Lost face: the core emits E-FDM-041 on every frame once the face has been
    // continuously undetected past its threshold (~1s by default). Rather than let
    // the attempt grind on producing no/low-confidence data (which is what happened
    // before this handling existed), restart the core session so measurement starts
    // clean when the face returns. Critical codes above take precedence.
    if (dataStatus && dataStatus.includes('E-FDM-041')) {
      await handleFaceLost();
      return;
    }

    // Face is present and tracked this frame: clear the consecutive-restart budget
    // so a brief, recovered loss earlier in the session doesn't count against a
    // later, independent one. FaceTracker.valid stays false during the sub-threshold
    // window right after a restart, so a permanently-absent face never resets here
    // and will reach the give-up cap.
    if (msg.metadata?.FaceTracker?.valid === true) {
      faceLostRestartCount = 0;
    }

    if (dataStatus && dataStatus.includes('S-HRT-061')) {
      hrCanEndEarly = true;
    }

    if (dataStatus && dataStatus.includes('S-RES-070')) {
      brCanEndEarly = true;
    }

    if (dataStatus && dataStatus.includes('S-BPR-090')) {
      bpCanEndEarly = true;
    }

    if (dataStatus && dataStatus.includes('S-SPO-100')) {
      spo2CanEndEarly = true;
    }

    const msgClone = { ...msg };
    // Guard against a missing dataStatus. The core can send a signs message
    // with no dataStatus array, and processDataStatus would throw on undefined.
    // Default to an empty array so a sparse message never breaks the emit.
    msgClone.dataStatus = processDataStatus(msg.dataStatus || []);

    emitter.emit('signsMessage', msgClone);

    if (canEndEarly() || (Date.now() - startTimestamp > (maxSessionTime * 1000))) {
      await stop();
    }
  };

  /**
   * Handle a lost-face (E-FDM-041) frame. Restart the current attempt so a
   * transient loss doesn't poison the measurement, up to maxFaceLostRestarts
   * consecutive times. Past that, give up: end the attempt and mark it invalid so
   * a consumer (e.g. a retry loop) can surface "couldn't detect a face" rather
   * than restart forever on an empty frame.
   */
  const handleFaceLost = async () => {
    // Set synchronously before any await so the per-frame E-FDM-041 storm and any
    // frames arriving mid-restart are ignored by the guard at the top of
    // handleSignsMsgs (one lost-face episode => one restart, not one per frame).
    restarting = true;
    faceLostRestartCount += 1;

    if (faceLostRestartCount > maxFaceLostRestarts) {
      emitter.emit('faceLostTimeout', {
        code: 'E-FDM-041',
        restarts: faceLostRestartCount - 1,
        message: 'Face not detected; ending attempt after repeated restarts',
      });
      // Terminal: end the attempt and flag it. stop() cancels frame ingestion, so
      // no further signs messages arrive; clearing `restarting` is just tidiness.
      await stop({ noValidMeasurements: true, endReason: 'face-lost' });
      restarting = false;
      return;
    }

    emitter.emit('faceLost', {
      code: 'E-FDM-041',
      restart: faceLostRestartCount,
      maxRestarts: maxFaceLostRestarts,
      message: 'Face not detected; restarting measurement',
    });

    await restartAttempt();
    restarting = false;
  };

  /**
   * Restart the core measurement session in place, keeping the same start()
   * lifecycle: end the current core session (discarding its partial result),
   * reset the per-attempt accumulators, and start a fresh core session with the
   * same selected vitals. The video frame ingestion loop is left running and no
   * 'stop' result is produced, so from the consumer's perspective this is one
   * continuous attempt whose clock simply restarts (mirrors vital-trac-app). It
   * is NOT a retry attempt, so app-level retry loops are unaffected.
   */
  const restartAttempt = async () => {
    // Discard the failed session's partial data.
    await workerManager.callMethod('endSession');

    brCanEndEarly = false;
    hrCanEndEarly = false;
    bpCanEndEarly = false;
    spo2CanEndEarly = false;
    currentSessionSignsMsgs = {};
    captureTimeUsed = false;

    const sessionTags = JSON.stringify({ vitals: currentSelectedVitals });
    await workerManager.callMethod('startSession', sessionTags);

    // Restart the session clock; clear the old interval first so intervals don't stack.
    clearInterval(countdownSetIntervalId);
    startCountdownLoop();
    startTimestamp = Date.now();

    emitter.emit('restart', { selectedVitals: currentSelectedVitals, restart: faceLostRestartCount });
  };

  /**
   * Determines if results are in for all vitals being measured and a session attempt can end
   * early before the max session time has elapsed
   */
  const canEndEarly = () => {
    if (!hrEarlyEndEnabled && !brEarlyEndEnabled && !bpEarlyEndEnabled && !spo2EarlyEndEnabled) {
      return false;
    }

    let enabledVitalsCollected = true;

    if (hrEarlyEndEnabled) {
      enabledVitalsCollected &= hrCanEndEarly;
    }
    if (brEarlyEndEnabled) {
      enabledVitalsCollected &= brCanEndEarly;
    }
    if (bpEarlyEndEnabled) {
      enabledVitalsCollected &= bpCanEndEarly;
    }
    if (spo2EarlyEndEnabled) {
      enabledVitalsCollected &= spo2CanEndEarly;
    }

    return enabledVitalsCollected;
  }

  /**
   * Set configuration to determine if a session attempt can end early if results for all
   * vitals are ready before the max session time has elapsed
   */
  const setEarlyEndEnabled = (selectedVitals, authorizedVitals) => {
    const selectedSet = new Set(selectedVitals);
    const authorizedSet = new Set(authorizedVitals);

    hrEarlyEndEnabled = (authorizedSet.has('PR_MD') ^ authorizedSet.has('PR_GW')) && (selectedSet.has('PR_MD') || selectedSet.has('PR_GW'));
    brEarlyEndEnabled = (authorizedSet.has('RR_MD') ^ authorizedSet.has('RR_GW')) && (selectedSet.has('RR_MD') || selectedSet.has('RR_GW'));
    bpEarlyEndEnabled = (authorizedSet.has('BP_MD') ^ authorizedSet.has('BP_GW')) && (selectedSet.has('BP_MD') || selectedSet.has('BP_GW'));
    spo2EarlyEndEnabled = (authorizedSet.has('SPO2_MD') ^ authorizedSet.has('SPO2_GW')) && (selectedSet.has('SPO2_MD') || selectedSet.has('SPO2_GW'));
  }

  /**
 * Ported over from vital-trac. Returns size and positioning of guide. These values are used
 * to instruct vital core where patients should be located in the images passed to vital core
 * @param {HTMLVideoElement} videoEl - html video element used to determin video orientation
 * @returns {Object} coordinates of guide
 */
const getGuideCoordinates = (videoEl) => {
  const videoOrientation = (videoEl.videoWidth > videoEl.videoHeight) ? 'landscape' : 'portrait';
  let width;
  let height;
  let x;
  const y = 80;

  // numbers based on positioning feedback from Rasool
  if (videoOrientation === 'portrait') {
    width = 240;
    height = 320;
    x = 120;
  } else {
    width = 192;
    height = 247;
    x = 224;
  }

  const result = { x, y, width, height };

  return result;
}

  /**
   * [Start] measurement session
   */
  const start = async (selectedVitals, videoEl) => {
    if (!initialized) {
      throw new Error('VitalClient not initialized. Call init() first.');
    }
    if (!vitalsHaveBeenAuthorized) {
      throw new Error('Vital Core Algorithm has not been authorized');
    }
    if (!videoEl || !(videoEl instanceof HTMLVideoElement)) {
      throw new Error('A video element is required by the start() function')
    }
    if (inPreviewMode) {
      await stopPreviewMode();
    }

    if (!guideCoordinatesSet) {
      const guideCoordinates = getGuideCoordinates(videoEl);
      await workerManager.callMethod('setFaceGuide', guideCoordinates);
      emitter.emit('guideCoordinates', guideCoordinates);
      guideCoordinatesSet = true;
    }

    brCanEndEarly = false;
    hrCanEndEarly = false;
    bpCanEndEarly = false;
    spo2CanEndEarly = false;

    // Reset signs messages collection for new session
    currentSessionSignsMsgs = {};
    captureTimeUsed = false;

    // Reset lost-face restart state for the new attempt and remember the selected
    // vitals so a mid-attempt restart can re-tag the fresh core session.
    restarting = false;
    faceLostRestartCount = 0;
    currentSelectedVitals = selectedVitals;

    setEarlyEndEnabled(selectedVitals, authorizedVitals);

    const sessionTags = JSON.stringify({ vitals: selectedVitals });
    await workerManager.callMethod('startSession', sessionTags);

    startCountdownLoop();
    startTimestamp = Date.now();
    statusManager.updateStatus({ measuring: true });
    emitter.emit('start', { selectedVitals });
    if (!videoFrameCallback) {
      ingestFrames(videoEl);
    }
  };

  /**
   * Start preview mode
   */
  const startPreviewMode = async (videoEl) => {
    if (!initialized) {
      throw new Error('VitalClient not initialized');
    }

    if (!guideCoordinatesSet) {
      const guideCoordinates = getGuideCoordinates(videoEl);
      await workerManager.callMethod('setFaceGuide', guideCoordinates);
      emitter.emit('guideCoordinates', guideCoordinates);
      guideCoordinatesSet = true;
    }

    try {
      await workerManager.callMethod('previewSession');
      inPreviewMode = true;
      ingestFrames(videoEl);
      emitter.emit('previewModeStart');
    } catch (e) {
      console.log('preview mode failed to start', e);
      throw new Error('Preview mode failed to start');
    }
  }

  /**
   * Stop preview mode
   */
  const stopPreviewMode = async () => {
    try {
      await workerManager.callMethod('endSession');
      inPreviewMode = false;
      emitter.emit('previewModeStop');
    } catch {
      throw new Error('Preview mode failed to stop');
    }
  }

  /**
   * Stop measurement session
   *
   * @param {Object} [options]
   * @param {boolean} [options.noValidMeasurements=false] - mark the result invalid
   *   (used by the lost-face give-up path so consumers can distinguish it).
   * @param {string|null} [options.endReason=null] - why the session ended
   *   (e.g. 'face-lost'); null for a normal early-end / max-time / manual stop.
   */
  const stop = async (options = {}) => {
    if (!initialized) {
      throw new Error('VitalClient not initialized');
    }

    const timestamp = Date.now();
    videoEl.cancelVideoFrameCallback(videoFrameCallback);
    videoFrameCallback = null;

    let wasmResult = await workerManager.callMethod('endSession');
    wasmResult = JSON.parse(wasmResult);
    startTimestamp = null;

    // Format result to match vital-trac-app's PRO submission structure exactly.
    //
    // CONTRACT (verified against vital-trac-app + the Mindset API):
    //   data.vitals        = the FULL final endSession message from the core.
    //                        vital-trac sends `vitals: vitalsMeasurements[0]`,
    //                        and that element IS the whole core message
    //                        (`lastSigns` = JSON.parse(endSession())). So `vitals`
    //                        carries `.vitals` (per-signal readings) PLUS
    //                        `.dataStatus` (incl. the "I-DBG-000-SessionEnd"
    //                        marker), `.sessionTime`, `.metadata`, and
    //                        `.processorTimes`. The API reads `data.vitals.vitals`
    //                        for per-signal values and archives the rest.
    //   finalVitalsMeasurementValues = per-signal best values (for score display).
    //
    // Previously this narrowed `vitals` to `{ vitals: wasmResult.vitals }`, which
    // dropped dataStatus/sessionTime/metadata/processorTimes and is why downstream
    // tooling could not see the EndSession record. Keeping the whole message does
    // NOT change any measured value; it only stops discarding fields the unchanged
    // core already produced.
    const vitalMeasurements = wasmResult.vitals || {};
    const result = {
      vitals: wasmResult, // FULL final endSession message (contains .vitals, .dataStatus, .sessionTime, .metadata, .processorTimes)
      signsMsgs: currentSessionSignsMsgs, // All collected per-frame signs messages (keyed by arrival time)
      prevVitals: [],
      prevSignsMsgs: [],
      finalVitalsMeasurementValues: vitalMeasurements, // Per-signal best values; required for score display
      noValidMeasurements: options.noValidMeasurements ?? false,
      endReason: options.endReason ?? null, // null for normal end; e.g. 'face-lost' on the give-up path
      webAppVersion: '1.0.0',
      frameCollectionMethod: 'web-sdk',
      captureTimeUsed, // Whether frames used media-clock captureTime vs Date.now() fallback
      vitalCoreVersion: coreVersion, // Cached at worker-ready; null if not yet available
      timestamp
    };

    statusManager.updateStatus({ measuring: false });
    clearInterval(countdownSetIntervalId);
    emitter.emit('stop', result);
    emitter.emit('timeLeft', 0);

    return result;
  };

  /**
   * Ingest video frames for processing
   */
  const ingestFrames = (videoElement) => {
    if (!initialized) {
      throw new Error('VitalClient not initialized');
    }

    videoEl = videoElement;

    const onVideoFrameCallback = async (_now, metadata) => {
      // Prefer the browser's media-clock capture time (precise per-frame instant)
      // over wall-clock Date.now(). This matches vital-trac-app and is the
      // timestamp the core uses for its interval/cadence math, so analysis tools
      // see true 60fps spacing instead of post-await main-thread jitter.
      // Fall back to Date.now() only when the browser does not supply captureTime.
      let timestamp;
      if (metadata && 'captureTime' in metadata) {
        timestamp = Math.round(metadata.captureTime);
        captureTimeUsed = true;
      } else {
        timestamp = Date.now();
      }

      const imgBitmap = await createImageBitmap(videoEl, 0, 0, videoEl.videoWidth, videoEl.videoHeight);

      workerManager.detectVitalsJS(imgBitmap, videoEl.videoWidth, videoEl.videoHeight, timestamp);

      videoFrameCallback = videoEl.requestVideoFrameCallback(onVideoFrameCallback);
    };

    videoFrameCallback = videoEl.requestVideoFrameCallback(onVideoFrameCallback);
  }

  function startCountdownLoop() {
    currentTimeLeft = maxSessionTime;
    emitter.emit('timeLeft', {
      timeLeft: currentTimeLeft,
      maxTime: maxSessionTime,
      percentComplete: 0,
      percentRemaining: 1,
    });

    countdownSetIntervalId = setInterval(() => {
      currentTimeLeft--;

      const percentRemaining = currentTimeLeft / maxSessionTime;
      const percentComplete = 1 - percentRemaining;

      emitter.emit('timeLeft', {
        timeLeft: currentTimeLeft,
        maxTime: maxSessionTime,
        percentComplete,
        percentRemaining,
      });

      if (currentTimeLeft <= 0) {
        clearInterval(countdownSetIntervalId);
      }
    }, 1000);
  }

  /**
   * Get version string from worker
   */
  const getVersion = async () => {
    if (!initialized) {
      throw new Error('VitalClient not initialized');
    }
    if (coreVersion != null) {
      return coreVersion;
    }
    coreVersion = await workerManager.callMethod('getVersionString');
    return coreVersion;
  };

  /**
   * Set face guide region
   */
  const setFaceGuide = async (faceGuide) => {
    if (!initialized) {
      throw new Error('VitalClient not initialized');
    }
    return await workerManager.callMethod('setFaceGuide', faceGuide);
  };

  /**
   * Enable/disable extended logging
   */
  const setExtendedLogs = async (enabled) => {
    if (!initialized) {
      throw new Error('VitalClient not initialized');
    }
    return await workerManager.callMethod('setExtendedLogs', enabled);
  };

  /**
   * Update SDK options
   */
  const updateOptions = (patch = {}) => {
    Object.assign(clientConfig, patch);
    // TODO: Notify worker of config changes
  };

  /**
   * Get current status
   */
  const getStatus = () => {
    return statusManager.getStatus();
  };

  /**
   * Register log handler
   */
  const onLog = (handler) => {
    if (workerManager) {
      workerManager.onLog(handler);
    }
  };

  /**
   * Unregister log handler
   */
  const offLog = () => {
    if (workerManager) {
      workerManager.offLog();
    }
  };

  /**
   * Auth vital core
   */
  const authorize = async () => {
    if (!vitalsHaveBeenAuthorized) {
      try {
        if (!clientConfig.authenticator) {
          throw new Error('Authenticator callback not provided in config');
        }

        let authorizeVitalsResp;
        try {
          authorizeVitalsResp = await workerManager.authorizeVitalCore(
            clientConfig.authenticator
          );
        } catch {
          throw new Error('Unable to verify the license key to use Vital-Trac.');
        }

        let vitalsToMeasure = [];
        maxSessionTime = 0;

        if (authorizeVitalsResp.status === 'authorized') {
          vitalsToMeasure = authorizeVitalsResp.vitals.reduce((vitalsToMeasure, vital) => {
            vitalsToMeasure.push(vital.tag);
            maxSessionTime = Math.max(vital.max_session_time, maxSessionTime);

            return vitalsToMeasure;
          }, []);
        } else {
          throw new Error('There is not an active license key to use Vital-Trac.');
        }

        if (!vitalsToMeasure.length) {
          throw new Error('The user or clinic is unlicensed. Please contact an administrator to enable access to Vital-Trac.');
        }

        emitter.emit('authorizedVitals', vitalsToMeasure);
        emitter.emit('maxSessionTime', maxSessionTime);

        authorizedVitals = vitalsToMeasure;
        vitalsHaveBeenAuthorized = true;
      } catch (e) {
        console.log('error calling workingManager.authorizeVitalCore');
        throw e;
      }
    } else {
      return { authorizedVitals, maxSessionTime }
    }
  }

  /**
   * Cleanup and destroy client
   */
  const destroy = () => {
    if (workerManager) {
      workerManager.destroy();
    }
    emitter.emit('destroyed');
    emitter.removeAllListeners();
    initialized = false;
    statusManager.reset();
  };

  return {
    // Core methods
    init,
    start,
    stop,
    destroy,
    startPreviewMode,
    stopPreviewMode,

    // Worker methods
    getVersion,
    authorize,
    setExtendedLogs,

    // Logging
    onLog,
    offLog,

    // Configuration
    updateOptions,
    getStatus,

    // Event emitter methods
    on: emitter.on.bind(emitter),
    once: emitter.once.bind(emitter),
    off: emitter.off.bind(emitter),

    // Expose for testing and advanced usage
    get initialized() { return initialized; },
  };
}
